import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchBookingConsole,
    updatePlans,
    addShop,
    updateShop,
    deleteShop,
    regenShopKey,
    updateShopType,
    updateBooking,
    updateBookingStatus,
    clearBookingError,
    clearBookingNotice,
} from "../../../Store/Slices/Admin/ManagerBooking";
import { fetchAllUsers } from "../../../Store/Slices/adminSlice";
import BookingShell from "../../Booking/components/BookingShell";
import OverviewTab from "./components/OverviewTab";
import ShopsTab from "./components/ShopsTab";
import ShopModal from "./components/ShopModal";
import BookingsTab from "./components/BookingsTab";
import PlansTab from "./components/PlansTab";
import Booking from "../../Booking";

const META = {
    overview: { title: "Overview", subtitle: "Platform totals and shops needing attention" },
    shops: { title: "Shops", subtitle: "Onboard shops and control their entitlements" },
    bookings: { title: "Bookings", subtitle: "Manage one shop's bookings at a time" },
    plans: { title: "Plans & limits", subtitle: "Set what Free and Pro include" },
};

/**
 * Booking owner console (route: /ap/bookings, admin only) in the Nekobiz
 * workspace layout: sidebar nav + header bar + content.
 * Onboard shops, control entitlements, set plan limits, inspect stats.
 */
export function BookingAdmin() {
    const dispatch = useDispatch();
    const { plans, shops, resourceTypes, resources, bookings, loading, error, notice } = useSelector(
        (state) => state.booking
    );

    const { allUsers } = useSelector((state) => state.admin);

    const [tab, setTab] = useState("overview");
    const [modal, setModal] = useState(null); // null | { mode: "add" } | { mode: "edit", id }
    const [saving, setSaving] = useState(false);
    const [impersonateId, setImpersonateId] = useState(null);

    useEffect(() => {
        dispatch(fetchBookingConsole());
        dispatch(fetchAllUsers());
    }, [dispatch]);

    const editingShop = modal?.mode === "edit"
        ? shops.find((s) => s.id === modal.id)
        : null;

    const impersonated = impersonateId ? shops.find((s) => s.id === impersonateId) || null : null;

    const pendingShops = useMemo(
        () => new Set(bookings.filter((b) => b.status === "pending").map((b) => b.ownerId)).size,
        [bookings]
    );

    const run = async (thunk) => {
        setSaving(true);
        const result = await dispatch(thunk);
        setSaving(false);
        return result;
    };

    const handleSaveShop = async (payload) => {
        const result = modal.mode === "add"
            ? await run(addShop(payload))
            : await run(updateShop({ id: modal.id, patch: payload }));
        const ok = modal.mode === "add"
            ? addShop.fulfilled.match(result)
            : updateShop.fulfilled.match(result);
        if (ok) setModal(null);
    };

    const handleToggleStatus = async (shop) => {
        const next = shop.status === "active" ? "suspended" : "active";
        const action = next === "suspended"
            ? `Suspend ${shop.business}? New bookings will be rejected (history kept).`
            : `Reactivate ${shop.business}?`;
        if (!window.confirm(action)) return;
        await run(updateShop({ id: shop.id, patch: { status: next } }));
    };

    const handleDelete = async (shop) => {
        if (!window.confirm(
            `Delete ${shop.business}? Its bookings stay in platform history, but the shop and its API key are gone.`
        )) return;
        await run(deleteShop(shop.id));
    };

    const handleRegenKey = async () => {
        if (!editingShop) return;
        if (!window.confirm("Issue a new API key? The old key stops working immediately.")) return;
        await run(regenShopKey(editingShop.id));
    };

    if (loading && shops.length === 0) {
        return (
            <div className="hk-page">
                <div className="hk-empty">
                    <div className="hk-empty-icon">🏪</div>
                    <div className="font-bold text-zinc-700">Loading booking console…</div>
                </div>
            </div>
        );
    }

    const sections = [
        {
            label: "Platform",
            items: [
                { id: "overview", icon: "⌂", label: "Overview" },
                { id: "shops", icon: "🏪", label: "Shops", badge: shops.length },
                { id: "bookings", icon: "◫", label: "Bookings", badge: pendingShops },
                { id: "plans", icon: "⚙", label: "Plans & limits" },
            ],
        },
    ];

    const meta = META[tab] || META.overview;

    if (impersonated) {
        return (
            <div className="booking-full">
                <Booking
                    impersonate={impersonated}
                    onExit={() => setImpersonateId(null)}
                />
            </div>
        );
    }

    return (
        <div className="booking-full">
            {(notice || error) && (
                <div className="max-w-[1400px] mx-auto w-full px-3 sm:px-6 pt-3 space-y-3">
                    {notice && (
                        <div className="alert alert-success alert-dismissible mb-0">
                            {notice}
                            <button type="button" className="btn-close" onClick={() => dispatch(clearBookingNotice())}></button>
                        </div>
                    )}
                    {error && (
                        <div className="alert alert-danger alert-dismissible mb-0">
                            {error}
                            <button type="button" className="btn-close" onClick={() => dispatch(clearBookingError())}></button>
                        </div>
                    )}
                </div>
            )}

            <BookingShell
                brand="Haki"
                sub="Booking platform"
                sections={sections}
                active={tab}
                onNav={setTab}
                title={meta.title}
                subtitle={meta.subtitle}
                actions={
                    <button className="hk-btn hk-btn-primary hk-btn-sm" onClick={() => setModal({ mode: "add" })}>
                        + Add shop
                    </button>
                }
                foot={
                    <div className="px-2 py-1 text-[11px] text-[#64748b]">
                        {shops.length} shops · {bookings.length} bookings total
                    </div>
                }
            >
                {tab === "overview" && (
                    <OverviewTab
                        plans={plans}
                        shops={shops}
                        resourceTypes={resourceTypes}
                        resources={resources}
                        bookings={bookings}
                        onAddShop={() => setModal({ mode: "add" })}
                        onConfigure={(id) => setModal({ mode: "edit", id })}
                    />
                )}
                {tab === "shops" && (
                    <ShopsTab
                        shops={shops}
                        resourceTypes={resourceTypes}
                        resources={resources}
                        bookings={bookings}
                        plans={plans}
                        onAdd={() => setModal({ mode: "add" })}
                        onConfigure={(id) => setModal({ mode: "edit", id })}
                        onToggleStatus={handleToggleStatus}
                        onDelete={handleDelete}
                        onImpersonate={(id) => { setTab("shops"); setImpersonateId(id); }}
                    />
                )}
                {tab === "bookings" && (
                    <BookingsTab
                        bookings={bookings}
                        shops={shops}
                        resources={resources}
                        onCancel={(id) => dispatch(updateBookingStatus({ id, status: "cancelled" }))}
                        onEdit={(id, patch) => dispatch(updateBooking({ id, patch }))}
                        onStatus={(id, status) => dispatch(updateBookingStatus({ id, status }))}
                    />
                )}
                {tab === "plans" && (
                    <PlansTab
                        plans={plans}
                        shops={shops}
                        resourceTypes={resourceTypes}
                        resources={resources}
                        saving={saving}
                        onSave={(p) => dispatch(updatePlans(p))}
                    />
                )}
            </BookingShell>

            {modal && (modal.mode === "add" || editingShop) && (
                <ShopModal
                    shop={modal.mode === "add" ? null : editingShop}
                    users={allUsers}
                    plans={plans}
                    resources={resources}
                    resourceTypes={resourceTypes}
                    saving={saving}
                    onClose={() => setModal(null)}
                    onSave={handleSaveShop}
                    onRegenKey={handleRegenKey}
                    onUpdateType={(id, patch) => dispatch(updateShopType({ id, patch }))}
                />
            )}
        </div>
    );
}

export default BookingAdmin;
